import React, { useEffect, useMemo, useRef, useState } from "react";
import { MapContainer, TileLayer, Marker, Polyline, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { api, resolveImageSrc } from "@/lib/api";
import { S } from "./styles";

const STEP_DEFS = {
  placed: { label: "Order placed", icon: "✅" },
  confirming_stock: { label: "Accepted · being prepared", icon: "📦" },
  picked_up: { label: "Picked up", icon: "🛵" },
  in_transit: { label: "In transit", icon: "🚚" },
  shipped: { label: "Shipped", icon: "📦" },
  delivered: { label: "Delivered", icon: "🏠" },
};
const STEP_FLOWS = {
  delivery_partner: ["placed", "confirming_stock", "picked_up", "delivered"],
  courier: ["placed", "shipped", "delivered"],
  manual: ["placed", "confirming_stock", "picked_up", "in_transit", "delivered"],
  default: ["placed", "confirming_stock", "picked_up", "delivered"],
};
const flowFor = (m) => STEP_FLOWS[m] || STEP_FLOWS.default;

// Custom Leaflet pin builders
const buildPin = (emoji, color) => L.divIcon({
  className: "qc-pin",
  html: `<div style="width:38px;height:38px;border-radius:50% 50% 50% 0;background:${color};transform:rotate(-45deg);display:flex;align-items:center;justify-content:center;box-shadow:0 6px 14px rgba(0,0,0,0.25);border:2.5px solid white;">
    <span style="transform:rotate(45deg);font-size:18px;line-height:1">${emoji}</span>
  </div>`,
  iconSize: [38, 38],
  iconAnchor: [19, 38],
});
const userPin = buildPin("🏠", "#0F3D2E");
const riderPin = buildPin("🛵", "#C97B3C");
const shopPin = buildPin("🏪", "#8B1A4A");

function FitAll({ points }) {
  const map = useMap();
  useEffect(() => {
    if (!points || points.length < 1) return;
    if (points.length === 1) { map.setView(points[0], 16); return; }
    const b = L.latLngBounds(points);
    map.fitBounds(b, { padding: [50, 50], maxZoom: 16 });
  }, [points, map]);
  return null;
}

export default function OrderTrackerModal({ orderId, onClose, showToast }) {
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const pollRef = useRef(null);

  const fetchOrder = async () => {
    try {
      const { data } = await api.get(`/orders/${orderId}`);
      setOrder(data);
    } catch (e) {
      showToast?.(e?.response?.data?.detail || "Could not load order");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrder();
    pollRef.current = setInterval(fetchOrder, 15000); // refresh every 15s
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId]);

  const method = order?.fulfillment_method || null;
  const flow = flowFor(method);
  const sIdx = order ? Math.max(0, flow.indexOf(order.status)) : 0;
  const showLiveMap = (method === "delivery_partner" || !method);
  const cancelled = order?.status === "cancelled";

  const points = useMemo(() => {
    if (!order) return [];
    const arr = [];
    if (order.delivery_lat != null && order.delivery_lng != null) arr.push([order.delivery_lat, order.delivery_lng]);
    if (order.delivery_lat_current != null && order.delivery_lng_current != null) arr.push([order.delivery_lat_current, order.delivery_lng_current]);
    return arr;
  }, [order]);

  const img = resolveImageSrc(order?.product_image);

  return (
    <div
      data-testid="order-tracker-modal"
      style={{ position: "fixed", inset: 0, zIndex: 1100, display: "flex", alignItems: "flex-end", justifyContent: "center", background: "rgba(0,0,0,0.55)" }}
      onClick={onClose}
    >
      <div onClick={(e) => e.stopPropagation()} style={{ background: "white", borderRadius: "22px 22px 0 0", maxWidth: 520, width: "100%", maxHeight: "94vh", overflowY: "auto" }}>
        <div style={{ width: 40, height: 4, background: "#E0DED2", borderRadius: 4, margin: "10px auto" }} />
        <div style={{ padding: 20 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "start" }}>
            <div>
              <div style={{ fontSize: 10, letterSpacing: 1.5, color: "#C97B3C", fontWeight: 700, textTransform: "uppercase" }}>Order tracking</div>
              <div style={{ ...S.fraunces, fontSize: 22, fontWeight: 700 }}>{cancelled ? "Order cancelled" : "Live status"}</div>
            </div>
            <button data-testid="order-tracker-close" onClick={onClose} style={{ background: "#F0EFE8", border: "none", borderRadius: "50%", width: 30, height: 30, fontSize: 14, cursor: "pointer" }}>✕</button>
          </div>

          {loading && !order && (<div style={{ padding: 30, textAlign: "center", color: "#666" }}>Loading…</div>)}

          {order && (
            <>
              {/* Product summary */}
              <div style={{ display: "flex", gap: 12, padding: 12, background: "#FAFAF5", border: "1px solid #EAE8DC", borderRadius: 14, marginTop: 14 }}>
                {img && (img.startsWith("http") || img.startsWith("data:")) && (
                  <img src={img} alt="" style={{ width: 56, height: 56, borderRadius: 10, objectFit: "cover", flexShrink: 0 }} />
                )}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 14, fontWeight: 700, lineHeight: 1.25 }}>{order.product_name}</div>
                  <div style={{ fontSize: 12, color: "#666", marginTop: 2 }}>{order.shop_name} · Qty {order.qty}</div>
                  <div style={{ ...S.fraunces, fontSize: 16, fontWeight: 800, color: "#0F3D2E", marginTop: 2 }}>₹{order.total} · COD</div>
                </div>
              </div>

              {/* Progress — steps adapt to the chosen fulfillment method */}
              {!cancelled && (
                <div data-testid="order-progress" style={{ marginTop: 18 }}>
                  {method && (
                    <div data-testid="order-fulfillment-method" style={{ fontSize: 11, fontWeight: 800, color: "#8B1A4A", marginBottom: 10 }}>
                      {method === "delivery_partner" && "🛵 Local delivery partner"}
                      {method === "courier" && "📦 Shipped via courier"}
                      {method === "manual" && "🧑‍🚀 Shop-managed delivery"}
                    </div>
                  )}
                  {flow.map((skey, i) => {
                    const step = STEP_DEFS[skey] || { label: skey, icon: "•" };
                    const done = i <= sIdx;
                    const active = i === sIdx;
                    return (
                      <div key={skey} data-testid={`order-step-${skey}`} style={{ display: "flex", gap: 12, alignItems: "start", paddingBottom: i === flow.length - 1 ? 0 : 12 }}>
                        <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                          <div style={{
                            width: 30, height: 30, borderRadius: "50%",
                            background: done ? "#0F3D2E" : "#E8E6D8",
                            color: done ? "white" : "#999",
                            display: "flex", alignItems: "center", justifyContent: "center",
                            fontSize: 14, fontWeight: 800,
                            boxShadow: active ? "0 0 0 4px rgba(15,61,46,0.18)" : "none",
                          }}>{done ? "✓" : i + 1}</div>
                          {i < flow.length - 1 && (
                            <div style={{ width: 2, height: 28, background: i < sIdx ? "#0F3D2E" : "#E8E6D8", marginTop: 2 }} />
                          )}
                        </div>
                        <div style={{ flex: 1, paddingTop: 4 }}>
                          <div style={{ fontSize: 14, fontWeight: active ? 800 : 600, color: done ? "#0F3D2E" : "#888" }}>
                            {step.icon} {step.label}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Courier tracking details */}
              {!cancelled && method === "courier" && order.tracking_id && (
                <div data-testid="order-courier-details" style={{ marginTop: 16, padding: 14, background: "#EEF4FF", border: "1px solid #BBD1F5", borderRadius: 12 }}>
                  <div style={{ fontSize: 10, letterSpacing: 1.4, color: "#1453A6", fontWeight: 800 }}>SHIPPED VIA COURIER</div>
                  <div style={{ fontSize: 14, fontWeight: 800, color: "#0F3D2E", marginTop: 5 }}>{order.courier_name}</div>
                  <div style={{ fontSize: 12.5, color: "#333", marginTop: 3 }}>Tracking ID: <strong data-testid="order-courier-tracking-id">{order.tracking_id}</strong></div>
                  {order.tracking_url && (
                    <a href={order.tracking_url} target="_blank" rel="noopener noreferrer" data-testid="order-courier-track-link"
                      style={{ display: "inline-block", marginTop: 8, fontSize: 12.5, fontWeight: 700, color: "#1453A6", textDecoration: "underline" }}>Track shipment →</a>
                  )}
                </div>
              )}

              {cancelled && (
                <div style={{ marginTop: 16, padding: 14, background: "#FFEFEF", border: "1px solid #F3B6B6", borderRadius: 12, color: "#9A2C2C", fontSize: 13, fontWeight: 600 }}>
                  This order was cancelled. If you have questions, please contact support.
                </div>
              )}

              {/* Live map — only for local delivery-partner (Zepto-style) orders */}
              {!cancelled && showLiveMap && points.length > 0 && (
                <div data-testid="order-map" style={{ marginTop: 18, borderRadius: 14, overflow: "hidden", border: "1px solid #EAE8DC" }}>
                  <MapContainer center={points[0]} zoom={15} style={{ height: 280, width: "100%" }} attributionControl={false}>
                    <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                    <FitAll points={points} />
                    {/* User home */}
                    <Marker position={[order.delivery_lat, order.delivery_lng]} icon={userPin} />
                    {/* Shop pin if we have it */}
                    {order.shop_lat != null && order.shop_lng != null && (
                      <Marker position={[order.shop_lat, order.shop_lng]} icon={shopPin} />
                    )}
                    {/* Rider live position */}
                    {order.delivery_lat_current != null && order.delivery_lng_current != null && (
                      <>
                        <Marker position={[order.delivery_lat_current, order.delivery_lng_current]} icon={riderPin} />
                        <Polyline positions={[[order.delivery_lat_current, order.delivery_lng_current], [order.delivery_lat, order.delivery_lng]]} pathOptions={{ color: "#C97B3C", weight: 4, dashArray: "8 6", opacity: 0.8 }} />
                      </>
                    )}
                  </MapContainer>
                  <div style={{ padding: "8px 12px", fontSize: 11, color: "#666", background: "#FAFAF5", display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 6 }}>
                    <span data-testid="order-rider-loc-status">
                      {order.delivery_lat_current != null ? `🛵 Rider is on the way` : `⏳ Rider not yet started`}
                    </span>
                    {order.delivery_loc_updated_at && (
                      <span style={{ color: "#888" }}>Updated {new Date(order.delivery_loc_updated_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</span>
                    )}
                  </div>
                </div>
              )}

              {/* Delivery address */}
              <div style={{ marginTop: 14, padding: 12, background: "#FAFAF5", border: "1px solid #EAE8DC", borderRadius: 12 }}>
                <div style={{ fontSize: 10, letterSpacing: 1.5, color: "#888", fontWeight: 700 }}>DELIVERING TO</div>
                <div style={{ fontSize: 13, marginTop: 4, color: "#333" }}>{order.delivery_address}</div>
                {order.user_mobile && <div style={{ fontSize: 12, color: "#666", marginTop: 2 }}>📞 {order.user_mobile}</div>}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
